/**
 * COLLAB-01 / SOCIAL-LAYER D — Multimedia realtime chat API client (client portal).
 *
 * Thin wrapper over the workspace-service /collab endpoints. Both the cabinet
 * (provider) and the client tenant use this same client; the backend scopes
 * thread visibility by tenant membership (owner / cabinet / client party) OR an
 * active chat_participants row (business↔business + group threads).
 *
 * The original 6 endpoints (listThreads / getThread / createThread / listMessages
 * / postMessage / markRead) are kept byte-compatible. The Social-Layer upgrade
 * adds: media upload, typing signal, participants, cursor pagination + search,
 * and a directory for the new-conversation participant picker.
 */
import { get, post, uploadFile } from './client'
import { API_ENDPOINTS } from './config'

/**
 * Thread kinds.
 *  - engagement / dossier / mandat / general → legacy per-engagement context_type
 *  - client_business → client↔business (default)
 *  - business_business → cabinet↔cabinet
 *  - group → N participants
 */
export type CollabContextType = 'engagement' | 'dossier' | 'mandat' | 'general'
export type ThreadKind = 'client_business' | 'business_business' | 'group'

export interface CollabAttachment {
  media_id?: string | null
  /** Public CDN URL (media.kygrces.com/{key}). */
  public_url?: string | null
  /** Some backends spell it `url`. */
  url?: string | null
  /** 'image' | 'video' | 'file' or a mime type. */
  kind?: string | null
  content_type?: string | null
  name?: string | null
  filename?: string | null
  size?: number | null
}

export interface CollabParticipant {
  id?: string
  thread_id?: string
  tenant_id: string
  user_id?: string | null
  name?: string | null
  role?: string | null
  status?: string | null
  last_read_at?: string | null
}

export interface CollabThread {
  id: string
  tenant_id: string
  cabinet_tenant_id?: string | null
  client_tenant_id?: string | null
  subject: string
  context_type: CollabContextType
  context_id?: string | null
  thread_kind?: ThreadKind | string | null
  status?: string | null
  created_by?: string | null
  created_at?: string | null
  last_message_at?: string | null
  last_message_preview?: string | null
  message_count: number
  unread_count?: number | null
  participants?: CollabParticipant[]
}

export interface CollabMessage {
  id: string
  thread_id: string
  tenant_id: string
  sender_id?: string | null
  sender_name?: string | null
  body: string
  attachments: CollabAttachment[]
  read_by: string[]
  created_at?: string | null
}

export interface ThreadCreatePayload {
  subject: string
  context_type?: CollabContextType
  context_id?: string | null
  cabinet_tenant_id?: string | null
  client_tenant_id?: string | null
  thread_kind?: ThreadKind
  participants?: Array<{ tenant_id: string; user_id?: string | null }>
  body?: string
}

/** A candidate counterparty for the new-conversation participant picker. */
export interface DirectoryParty {
  tenant_id: string
  name: string
  kind: 'business' | 'client'
  user_id?: string | null
}

interface ThreadList {
  items: CollabThread[]
  total: number
}
interface MessageList {
  items: CollabMessage[]
  total?: number
  next_cursor?: string | null
  nextCursor?: string | null
  cursor?: string | null
}

export interface MessagePage {
  items: CollabMessage[]
  nextCursor: string | null
}

export interface MediaUploadResult {
  /** Present when the backend created + returned the full message. */
  message: CollabMessage | null
  /** Present when the backend returned just the stored attachment. */
  attachment: CollabAttachment | null
}

// ── Threads ───────────────────────────────────────────────────────────────────

export async function listThreads(params?: {
  context_type?: CollabContextType
  context_id?: string
  q?: string
}): Promise<CollabThread[]> {
  const res = await get<ThreadList>(API_ENDPOINTS.collab.threads, { params })
  return res?.items ?? []
}

export async function getThread(id: string): Promise<CollabThread> {
  return get<CollabThread>(API_ENDPOINTS.collab.thread(id))
}

export async function createThread(payload: ThreadCreatePayload): Promise<CollabThread> {
  return post<CollabThread, ThreadCreatePayload>(API_ENDPOINTS.collab.createThread, payload)
}

export async function listParticipants(threadId: string): Promise<CollabParticipant[]> {
  try {
    const res = await get<{ items?: CollabParticipant[] } | CollabParticipant[]>(
      API_ENDPOINTS.collab.participants(threadId),
    )
    if (Array.isArray(res)) return res
    return res?.items ?? []
  } catch {
    // Endpoint not deployed everywhere yet — degrade to no explicit participants.
    return []
  }
}

// ── Messages ──────────────────────────────────────────────────────────────────

/** Legacy array form (kept for callers that don't paginate). */
export async function listMessages(threadId: string): Promise<CollabMessage[]> {
  const res = await get<MessageList>(API_ENDPOINTS.collab.messages(threadId))
  return normalizeMessages(res?.items)
}

/** Cursor-paginated + searchable message fetch. */
export async function listMessagesPage(
  threadId: string,
  opts?: { limit?: number; cursor?: string | null; q?: string },
): Promise<MessagePage> {
  const params: Record<string, string | number> = {}
  if (opts?.limit) params.limit = opts.limit
  if (opts?.cursor) params.cursor = opts.cursor
  if (opts?.q && opts.q.trim()) params.q = opts.q.trim()
  const res = await get<MessageList>(API_ENDPOINTS.collab.messages(threadId), { params })
  const nextCursor = res?.next_cursor ?? res?.nextCursor ?? res?.cursor ?? null
  return { items: normalizeMessages(res?.items), nextCursor }
}

export async function postMessage(
  threadId: string,
  body: string,
  attachments: CollabAttachment[] = [],
): Promise<CollabMessage> {
  const msg = await post<CollabMessage>(API_ENDPOINTS.collab.postMessage(threadId), {
    body,
    attachments,
  })
  return normalizeMessage(msg)
}

export async function markRead(
  threadId: string,
  messageIds?: string[],
): Promise<{ updated: number }> {
  return post<{ updated: number }>(API_ENDPOINTS.collab.markRead(threadId), {
    message_ids: messageIds ?? null,
  })
}

// ── Multimedia + presence (Social-Layer additions) ──────────────────────────────

/**
 * Upload media to a thread. Multipart → R2 (media.kygrces.com). The backend
 * records the attachment on a message; response shape varies (full message vs
 * bare attachment) so we normalize both.
 */
export async function uploadMedia(
  threadId: string,
  file: File,
  caption?: string,
  onProgress?: (pct: number) => void,
): Promise<MediaUploadResult> {
  const raw = await uploadFile<unknown>(
    API_ENDPOINTS.collab.media(threadId),
    file,
    onProgress,
    { caption: caption ?? '', kind: guessKind(file.type, file.name) },
  )
  return normalizeUpload(raw)
}

/** Ephemeral typing signal — best-effort, never throws. */
export async function sendTyping(threadId: string): Promise<void> {
  try {
    await post(API_ENDPOINTS.collab.typing(threadId), {})
  } catch {
    /* typing is best-effort; a missing endpoint must not surface an error */
  }
}

// ── Directory (participant picker) ──────────────────────────────────────────────

/**
 * Candidate counterparties for a new conversation. In the client portal these
 * are the client's providers/cabinets (an ACTIVE relationship gives the party
 * its business tenant_id).
 */
export async function listDirectory(): Promise<DirectoryParty[]> {
  try {
    const res = await get<
      | Array<{ cabinet_tenant_id?: string; name?: string; client_id?: string }>
      | { items?: Array<{ cabinet_tenant_id?: string; name?: string }> }
    >('/clients/portal/prestataires')
    const rows = Array.isArray(res) ? res : (res?.items ?? [])
    return rows
      .filter((r) => !!r.cabinet_tenant_id)
      .map((r) => ({
        tenant_id: String(r.cabinet_tenant_id),
        name: r.name || 'Cabinet',
        kind: 'business' as const,
      }))
  } catch {
    return []
  }
}

// ── Normalizers ─────────────────────────────────────────────────────────────────

function normalizeMessage(m: unknown): CollabMessage {
  const row = (m ?? {}) as Record<string, unknown>
  return {
    id: String(row.id ?? ''),
    thread_id: String(row.thread_id ?? ''),
    tenant_id: String(row.tenant_id ?? ''),
    sender_id: (row.sender_id as string) ?? null,
    sender_name: (row.sender_name as string) ?? null,
    body: typeof row.body === 'string' ? row.body : '',
    attachments: normalizeAttachments(row.attachments),
    read_by: Array.isArray(row.read_by) ? (row.read_by as string[]) : [],
    created_at: (row.created_at as string) ?? null,
  }
}

function normalizeMessages(items: unknown): CollabMessage[] {
  if (!Array.isArray(items)) return []
  return items.map(normalizeMessage)
}

function normalizeAttachments(raw: unknown): CollabAttachment[] {
  if (!Array.isArray(raw)) return []
  return raw.map((a) => {
    const o = (a ?? {}) as Record<string, unknown>
    return {
      media_id: (o.media_id as string) ?? null,
      public_url: (o.public_url as string) ?? (o.url as string) ?? null,
      url: (o.url as string) ?? null,
      kind: (o.kind as string) ?? (o.content_type as string) ?? null,
      content_type: (o.content_type as string) ?? null,
      name: (o.name as string) ?? (o.filename as string) ?? null,
      filename: (o.filename as string) ?? null,
      size: typeof o.size === 'number' ? (o.size as number) : null,
    }
  })
}

function normalizeUpload(raw: unknown): MediaUploadResult {
  const o = (raw ?? {}) as Record<string, unknown>
  const inner = (o.message ?? o.item ?? o.data ?? o) as Record<string, unknown>
  // Looks like a message?
  if (inner && (inner.thread_id || Array.isArray(inner.attachments) || inner.body != null)) {
    if (inner.id || inner.thread_id) {
      return { message: normalizeMessage(inner), attachment: null }
    }
  }
  // Otherwise treat as a bare attachment.
  const attSrc = (o.attachment ?? o) as Record<string, unknown>
  if (attSrc.public_url || attSrc.url || attSrc.media_id) {
    return { message: null, attachment: normalizeAttachments([attSrc])[0] }
  }
  return { message: null, attachment: null }
}

/** Rough media-kind classification for the bubble renderer. */
export function guessKind(contentType?: string | null, name?: string | null): string {
  const ct = (contentType || '').toLowerCase()
  if (ct.startsWith('image/')) return 'image'
  if (ct.startsWith('video/')) return 'video'
  if (ct.startsWith('audio/')) return 'audio'
  const ext = (name || '').split('.').pop()?.toLowerCase() || ''
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'avif'].includes(ext)) return 'image'
  if (['mp4', 'webm', 'mov', 'm4v'].includes(ext)) return 'video'
  if (['mp3', 'wav', 'ogg', 'm4a'].includes(ext)) return 'audio'
  return 'file'
}
